import React, { useState } from 'react';
import {
  Box, Text, HStack, Checkbox, IconButton, Tooltip, Spinner,
  Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalFooter,
  Button, useDisclosure,
} from '@chakra-ui/react';
import { ChevronDownIcon, DeleteIcon } from '@chakra-ui/icons';
import { getColumnStyle } from '../../../utils/tableUtils';

/**
 * Table header for company table
 * Displays column headers with select-all checkbox
 * 
 * @param {Object} props
 * @param {Object} props.columns - Column configuration object
 * @param {number} props.selectedCount - Number of selected rows
 * @param {number} props.totalCount - Total number of rows
 * @param {Function} props.onSelectAll - Select all handler
 * @param {Array} props.fieldDefinitions - Custom field definitions
 * @param {number} props.minWidth - Minimum width for the header
 * @param {Function} props.onContinueGeneration - Handler for continue generation button click
 * @param {boolean} props.isStatusBarVisible - Whether the status bar is currently visible
 * @param {Function} props.onDeleteField - Called with fieldDef when the user confirms deletion
 * @param {boolean} props.isSelectingAll - Shows mini loader while selecting all rows
 */
const CompanyTableHeader = ({
  columns,
  selectedCount,
  totalCount,
  onSelectAll,
  fieldDefinitions = [],
  minWidth,
  onContinueGeneration,
  isStatusBarVisible = false,
  onDeleteField,
  isSelectingAll = false,
}) => {
  const isAllSelected = totalCount > 0 && selectedCount === totalCount;
  const isIndeterminate = selectedCount > 0 && selectedCount < totalCount;
  const [hoveredField, setHoveredField] = useState(null);

  const [pendingDeleteField, setPendingDeleteField] = useState(null);
  const { isOpen: isDeleteModalOpen, onOpen: onDeleteModalOpen, onClose: onDeleteModalClose } = useDisclosure();

  const handleDeleteClick = (e, fieldDef) => {
    e.stopPropagation();
    setPendingDeleteField(fieldDef);
    onDeleteModalOpen();
  };

  const handleConfirmDelete = () => {
    if (pendingDeleteField && onDeleteField) {
      onDeleteField(pendingDeleteField);
    }
    setPendingDeleteField(null);
    onDeleteModalClose();
  };

  return (
    <Box
      position="sticky"
      top={0}
      zIndex={10}
      bg="#0a0a0a"
      border="1px solid"
      borderColor="rgba(255,255,255,0.08)"
      minH="50px"
      minW={minWidth ? `${minWidth}px` : "100%"}
      w="100%"
    >
      <HStack spacing={0} minH="50px" align="stretch" w="100%" minW="100%">
        <Box {...getColumnStyle(columns.checkbox)} px={4} borderRight="1px solid" borderColor="rgba(255,255,255,0.08)" display="flex" alignItems="center" justifyContent="center" py={2}>
          {isSelectingAll ? (
            <Spinner size="xs" color="rgba(255,255,255,0.4)" thickness="2px" />
          ) : (
            <Checkbox
              isChecked={isAllSelected}
              isIndeterminate={isIndeterminate}
              onChange={(e) => onSelectAll(e.target.checked)}
              colorScheme="whiteAlpha"
            />
          )}
        </Box>
        <Box {...getColumnStyle(columns.name)} px={4} borderRight="1px solid" borderColor="rgba(255,255,255,0.08)" display="flex" alignItems="center" py={2}>
          <Text fontWeight="600" color="rgba(255,255,255,0.7)" fontSize="xs" wordBreak="break-word" textTransform="uppercase" letterSpacing="0.05em">Company Name</Text>
        </Box>
        <Box {...getColumnStyle(columns.headcount)} px={4} borderRight="1px solid" borderColor="rgba(255,255,255,0.08)" display="flex" alignItems="center" py={2}>
          <Text fontWeight="600" color="rgba(255,255,255,0.7)" fontSize="xs" wordBreak="break-word" textTransform="uppercase" letterSpacing="0.05em">Headcount</Text>
        </Box>
        <Box {...getColumnStyle(columns.revenue)} px={4} borderRight="1px solid" borderColor="rgba(255,255,255,0.08)" display="flex" alignItems="center" py={2}>
          <Text fontWeight="600" color="rgba(255,255,255,0.7)" fontSize="xs" wordBreak="break-word" textTransform="uppercase" letterSpacing="0.05em">Revenue</Text>
        </Box>
        <Box {...getColumnStyle(columns.location)} px={4} borderRight="1px solid" borderColor="rgba(255,255,255,0.08)" display="flex" alignItems="center" py={2}>
          <Text fontWeight="600" color="rgba(255,255,255,0.7)" fontSize="xs" wordBreak="break-word" textTransform="uppercase" letterSpacing="0.05em">Location</Text>
        </Box>
        {fieldDefinitions.map((fieldDef, idx) => (
          <Box
            key={`custom_header_${idx}`}
            {...getColumnStyle(columns[`custom_${idx}`])}
            px={4}
            borderRight="1px solid"
            borderColor="rgba(255,255,255,0.08)"
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            py={2}
            position="relative"
            onMouseEnter={() => setHoveredField(fieldDef.field_id)}
            onMouseLeave={() => setHoveredField(null)}
          >
            <Text
              fontWeight="600"
              color="rgba(255,255,255,0.7)"
              fontSize="xs"
              wordBreak="break-word"
              whiteSpace="normal"
              lineHeight="1.4"
              textTransform="uppercase"
              letterSpacing="0.05em"
              flex={1}
              minW={0}
            >
              {fieldDef.display_name}
            </Text>
            {hoveredField === fieldDef.field_id && (
              <HStack spacing={0.5} flexShrink={0}>
                {onContinueGeneration && !isStatusBarVisible && (
                  <Tooltip label="Continue generating values for this field" placement="top" hasArrow>
                    <IconButton
                      icon={<ChevronDownIcon boxSize="2.5" />}
                      size="xs"
                      w="18px"
                      h="18px"
                      minW="18px"
                      p={0}
                      variant="ghost"
                      aria-label="Continue generation"
                      color="rgba(255,255,255,0.5)"
                      bg="rgba(255,255,255,0.08)"
                      _hover={{ bg: "rgba(255,255,255,0.15)", color: "#fafafa" }}
                      _focus={{ boxShadow: "none" }}
                      onClick={() => onContinueGeneration(fieldDef)}
                    />
                  </Tooltip>
                )}
                {onDeleteField && (
                  <Tooltip label="Delete column" placement="top" hasArrow>
                    <IconButton
                      icon={<DeleteIcon boxSize="2.5" />}
                      size="xs"
                      w="18px"
                      h="18px"
                      minW="18px"
                      p={0}
                      variant="ghost"
                      aria-label="Delete field"
                      color="rgba(239,68,68,0.7)"
                      _hover={{ bg: "rgba(239,68,68,0.1)", color: "rgba(239,68,68,0.9)" }}
                      _focus={{ boxShadow: "none" }}
                      onClick={(e) => handleDeleteClick(e, fieldDef)}
                    />
                  </Tooltip>
                )}
              </HStack>
            )}
          </Box>
        ))}
        <Box {...getColumnStyle(columns.links)} px={4} display="flex" alignItems="center" py={2}>
          <Text fontWeight="600" color="rgba(255,255,255,0.7)" fontSize="xs" wordBreak="break-word" textTransform="uppercase" letterSpacing="0.05em">Links</Text>
        </Box>
      </HStack>

      <Modal isOpen={isDeleteModalOpen} onClose={onDeleteModalClose} isCentered size="sm">
        <ModalOverlay bg="blackAlpha.700" />
        <ModalContent bg="#0a0a0a" color="#fafafa" border="1px solid rgba(255,255,255,0.10)" borderRadius="12px">
          <ModalHeader fontSize="lg" fontWeight="600" pb={3} borderBottomWidth="1px" borderColor="rgba(255,255,255,0.08)">Delete column</ModalHeader>
          <ModalBody>
            <Text color="#a3a3a3" fontSize="sm">
              Are you sure you want to delete the <strong style={{ color: '#fafafa' }}>{pendingDeleteField?.display_name}</strong> column?
              All values will be removed. This cannot be undone.
            </Text>
          </ModalBody>
          <ModalFooter gap={2} borderTopWidth="1px" borderColor="rgba(255,255,255,0.08)">
            <Button
              variant="ghost"
              onClick={onDeleteModalClose}
              color="#a3a3a3"
              borderRadius="full"
              _hover={{ color: '#fafafa', bg: 'rgba(255,255,255,0.06)' }}
              _focus={{ boxShadow: 'none' }}
            >
              Cancel
            </Button>
            <Button
              bg="rgba(239,68,68,0.15)"
              color="rgba(239,68,68,0.85)"
              border="1px solid rgba(239,68,68,0.25)"
              borderRadius="full"
              fontWeight="600"
              _hover={{ bg: 'rgba(239,68,68,0.25)', color: 'rgba(239,68,68,1)' }}
              _focus={{ boxShadow: 'none' }}
              onClick={handleConfirmDelete}
            >
              Delete
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default CompanyTableHeader;
